import { z } from 'zod';
import type { BlockDefinition } from '../types';
import { sanitizedHtmlProps } from '../lib/sanitize';

export const featureSplitSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  titleMute: z.string().max(160).optional(),
  cards: z
    .array(
      z.object({
        variant: z.enum(['accel', 'ma']).default('accel'),
        tag: z.string().min(1).max(80),
        title: z.string().min(1).max(300),
        description: z.string().max(500),
        image: z.string().max(500).optional(),
        imageAlt: z.string().max(160).optional(),
        stats: z
          .array(
            z.object({
              value: z.string().max(40),
              label: z.string().max(80),
            }),
          )
          .max(4)
          .optional(),
        ctaLabel: z.string().max(80),
        ctaHref: z.string().min(1).max(500),
      }),
    )
    .min(2)
    .max(2),
});

export type FeatureSplitData = z.infer<typeof featureSplitSchema>;

export function FeatureSplitRenderer({
  eyebrow,
  title,
  titleMute,
  cards,
}: FeatureSplitData) {
  const safeCards = Array.isArray(cards) ? cards : [];
  return (
    <section className="v7-section v5-uses-v7" id="productos">
      <div className="container">
        <div className="v7-section-head">
          {eyebrow ? <span className="v7-eyebrow">{eyebrow}</span> : null}
          <h2 className="v7-h2">
            {title}
            {titleMute ? <> <span className="v7-h2-mute">{titleMute}</span></> : null}
          </h2>
        </div>
        <div className="v7-bento">
          {safeCards.map((c, i) => (
            <div key={i} className={`v7-card v7-card-${c.variant} v7-card-xl${c.image ? ' v7-card-has-media' : ''}`}>
              <div className="v7-card-glow"></div>
              {c.image ? (
                <div className="v7-card-media">
                  <img src={c.image} alt={c.imageAlt ?? c.tag} loading="lazy" />
                </div>
              ) : null}
              <div className="v7-card-tag">
                <span className={`v7-tag-dot ${c.variant}`}></span>
                {c.tag}
              </div>
              <h3 className="v7-card-title" dangerouslySetInnerHTML={sanitizedHtmlProps(c.title)} />
              <p className="v7-card-desc">{c.description}</p>
              {c.stats && c.stats.length > 0 ? (
                <div className="v7-card-mini-stats">
                  {c.stats.map((s, j) => (
                    <div key={j}>
                      <strong>{s.value}</strong>
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              <a href={c.ctaHref} className="v7-card-cta">
                {c.ctaLabel} →
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const featureSplitBlock: BlockDefinition<FeatureSplitData> = {
  meta: {
    type: 'feature-split',
    label: 'Bento productos / 2 servicios (v7)',
    description: 'Bento XL de 2 tarjetas grandes (Aceleración + M&A) con glow, mini-stats y CTA.',
    category: 'content',
    icon: 'split-square-vertical',
  },
  schema: featureSplitSchema,
  defaults: {
    eyebrow: '— Servicios',
    title: 'Una plataforma.',
    titleMute: 'Todo el ciclo empresarial.',
    cards: [
      {
        variant: 'accel',
        tag: 'Aceleración',
        title: 'Capital, mentor sénior<br>y red para fundadores.',
        description: 'Programa intensivo de 3 meses con capital semilla, mentor sénior dedicado y Demo Day frente a +50 inversores.',
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=700&fit=crop&q=80&auto=format',
        imageAlt: 'Equipo de fundadores trabajando en su startup',
        stats: [
          { value: '+50', label: 'aceleradas' },
          { value: '+€8M', label: 'levantados' },
          { value: '+200', label: 'mentores' },
        ],
        ctaLabel: 'Aplica al programa',
        ctaHref: '/para-startups',
      },
      {
        variant: 'ma',
        tag: 'Compraventa M&A',
        title: 'Vende, compra o valora<br>tu empresa con sénior.',
        description: 'Asesoramiento integral en operaciones M&A. NDA desde el primer contacto. +1.500 compradores en cartera activa.',
        image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&h=700&fit=crop&q=80&auto=format',
        imageAlt: 'Apretón de manos cerrando una operación',
        stats: [
          { value: '+120', label: 'operaciones' },
          { value: '+€115M', label: 'movilizados' },
          { value: '84%', label: 'tasa éxito' },
        ],
        ctaLabel: 'Conoce el servicio',
        ctaHref: '/compraventa',
      },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'titleMute', label: 'Continuación (color mute)', type: 'text', max: 160 },
    {
      name: 'cards',
      label: 'Tarjetas (2)',
      type: 'array',
      max: 2,
      min: 2,
      itemFields: [
        {
          name: 'variant',
          label: 'Variante color',
          type: 'select',
          options: [
            { label: 'Aceleración (lima)', value: 'accel' },
            { label: 'M&A (azul)', value: 'ma' },
          ],
        },
        { name: 'tag', label: 'Tag', type: 'text', required: true, max: 80 },
        { name: 'title', label: 'Título (HTML permitido)', type: 'text', required: true, max: 300 },
        { name: 'description', label: 'Descripción', type: 'textarea', required: true, max: 500 },
        { name: 'image', label: 'Imagen de cabecera', type: 'image' },
        { name: 'imageAlt', label: 'Texto alternativo imagen', type: 'text', max: 160 },
        {
          name: 'stats',
          label: 'Mini stats',
          type: 'array',
          max: 4,
          itemFields: [
            { name: 'value', label: 'Valor', type: 'text', required: true, max: 40 },
            { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 80 },
          ],
        },
        { name: 'ctaLabel', label: 'Texto CTA', type: 'text', required: true, max: 80 },
        { name: 'ctaHref', label: 'URL CTA', type: 'url', required: true },
      ],
    },
  ],
  Renderer: FeatureSplitRenderer,
};
